import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  PictureAsPdf,
  Print,
  Visibility,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { getApiBaseUrl } from "../../../helpers/apiUrl";
import ensureLightReportPreviewHtml from "../../../helpers/ensureLightReportPreviewHtml";
import toast from "../../../helpers/toast";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import {
  useGetCurrentTermQuery,
  useGetSessionsPaginatedQuery,
} from "../settings/session/sessionApiSlice";
import { useGetTemplatesPaginatedQuery } from "../Templates/templatesApiSlice";
import {
  useCancelReportMutation,
  useCreateBroadsheetMutation,
  useDeleteBroadsheetMutation,
  useGetReportStatusQuery,
  useLazyGetBroadsheetQuery,
} from "./classBroadsheetApiSlice";

const validationSchema = yup.object({
  class: yup.object().required("Required"),
  session: yup.object().required("Required"),
  term: yup.string().required("Required"),
});

function ClassBroadsheet() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [createBroadsheet, { isLoading: isCreatingBroadsheet }] =
    useCreateBroadsheetMutation();
  const [fetchBroadsheets, { isFetching: isFetchingBroadsheets }] =
    useLazyGetBroadsheetQuery();
  const [deleteBroadsheet] = useDeleteBroadsheetMutation();
  const [cancelReport, { isLoading: isCancelling }] = useCancelReportMutation();

  const [broadsheets, setBroadsheets] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [reportTaskId, setReportTaskId] = useState(null);
  const [reportTaskDone, setReportTaskDone] = useState(false);

  const handleCancel = async () => {
    if (!reportTaskId) return;
    try {
      await cancelReport(reportTaskId).unwrap();
      setReportTaskId(null);
      setReportTaskDone(true);
      setReportProgress(null);
      pendingParamsRef.current = null;
      toast.info("Broadsheet generation cancelled.");
    } catch {
      toast.error("Failed to cancel.");
    }
  };
  const [reportProgress, setReportProgress] = useState(null);
  const pendingParamsRef = useRef(null);

  const isPolling = reportTaskId && !reportTaskDone;
  const { data: reportStatusData } = useGetReportStatusQuery(reportTaskId, {
    skip: !reportTaskId || reportTaskDone,
    pollingInterval: isPolling ? 3000 : 0,
  });

  useEffect(() => {
    if (!reportStatusData) return;
    const { state, info } = reportStatusData;
    if (state === "PROGRESS" && info) {
      setReportProgress(info);
    } else if (state === "SUCCESS") {
      setReportTaskDone(true);
      setReportProgress(null);
      toast.success("Broadsheet generated successfully");
      if (pendingParamsRef.current) {
        const { classroom, params } = pendingParamsRef.current;
        fetchBroadsheets(params).then(({ data: results }) => {
          if (results && results.length > 0) {
            setBroadsheets(results.map((r) => ({ classroom, broadsheet: r })));
          }
        });
        pendingParamsRef.current = null;
      }
      setReportTaskId(null);
    } else if (state === "FAILURE") {
      setReportTaskDone(true);
      setReportProgress(null);
      const errMsg =
        typeof info === "string" ? info : "Broadsheet generation failed";
      toast.error(errMsg);
      setReportTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStatusData]);

  const handleDelete = async (item) => {
    const willDelete = window.confirm(
      "This will permanently delete this broadsheet.",
    );
    if (willDelete) {
      try {
        await deleteBroadsheet({ id: item.broadsheet?.id }).unwrap();
        setBroadsheets((prev) =>
          prev.filter((r) => r.broadsheet?.id !== item.broadsheet?.id),
        );
        toast.success("Broadsheet has been deleted.");
      } catch (error) {
        toast.error(error?.message || "Failed to delete broadsheet");
      }
    }
  };

  const handleFilePreview = (url) => {
    setPreviewUrl(url);
  };

  const ReportPreviewModal = ({ url, open, onClose }) => {
    const [iframeLoading, setIframeLoading] = useState(true);
    const googleDocsUrl = url
      ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
      : null;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          Broadsheet Preview
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            position: "relative",
            bgcolor: "#fff",
          }}
        >
          {iframeLoading && googleDocsUrl && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.85)",
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {googleDocsUrl && (
            <iframe
              key={url}
              src={googleDocsUrl}
              title="Broadsheet Preview"
              width="100%"
              height="100%"
              style={{ border: "none", backgroundColor: "#fff" }}
              onLoad={() => setIframeLoading(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const handlePreview = async (previewValues = formik.values) => {
    const {
      class: cls,
      term,
      template,
      calculation,
      assessment_type,
    } = previewValues;
    if (!cls || !term) {
      toast.error("Please select a class and term to preview");
      return;
    }
    try {
      setIsPreviewing(true);
      const BASE_URL = getApiBaseUrl();
      const token = localStorage
        .getItem("access_token")
        .replace('"', "")
        .replace('"', "");
      const url = `/report/broadsheet?preview=html&classroom_id=${cls.id}&term_id=${term}&calculation=${calculation || "standard"}&assessment_type=${assessment_type || "all"}${template ? `&template_id=${template}` : ""}`;
      const req = await fetch(BASE_URL + url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsPreviewing(false);
      if (!req.ok) {
        return toast.error("Error loading broadsheet preview");
      }
      const htmlText = await req.text();
      setPreviewHtml(ensureLightReportPreviewHtml(htmlText));
      setOpenPreview(true);
    } catch (error) {
      setIsPreviewing(false);
      toast.error("Error loading broadsheet preview");
    }
  };

  const formik = useFormik({
    initialValues: {
      class: "",
      session: "",
      term: "",
      template: "",
      outputMode: "download",
      calculation: "standard",
      assessment_type: "all",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (values.outputMode === "preview") {
        await handlePreview(values);
        return;
      }

      if (!values.template) {
        const { data: results, error } = await fetchBroadsheets({
          classroom_id: values.class.id,
          term_id: values.term,
          calculation: values.calculation,
          assessment_type: values.assessment_type,
        });
        if (error || !results || results.length === 0) {
          toast.info(
            "No broadsheets found for this class and term. Please select a template to generate one.",
          );
          return;
        }
        setBroadsheets(
          results.map((r) => ({ classroom: values.class, broadsheet: r })),
        );
        return;
      }
      try {
        const body = {
          term_id: values.term,
          classroom_id: values.class.id,
          template_id: values.template,
          calculation: values.calculation,
          assessment_type: values.assessment_type,
        };

        const response = await createBroadsheet(body).unwrap();
        if (response.id) {
          setBroadsheets([{ classroom: values.class, broadsheet: response }]);
          toast.success("broadsheet generated successfully");
        } else if (response.task_id) {
          pendingParamsRef.current = {
            classroom: values.class,
            params: {
              classroom_id: values.class.id,
              term_id: values.term,
              calculation: values.calculation,
              assessment_type: values.assessment_type,
            },
          };
          setReportTaskDone(false);
          setReportTaskId(response.task_id);
          toast.info("Generating broadsheet...");
        } else if (response.message) {
          toast.success(
            `Generating ${values.class.name} Broadsheet..., come back later`,
          );
        } else {
          toast.error("Broadsheet generation failed");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const [classroomSearch, setClassroomSearch] = React.useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({
    page: 1,
    per_page: 10,
    q: classroomSearch,
  });
  const classrooms = classroomsData?.classrooms ?? [];
  const [sessionSearch, setSessionSearch] = React.useState("");
  const { data: sessionsData } = useGetSessionsPaginatedQuery({
    page: 1,
    per_page: 5,
    q: sessionSearch,
  });
  const sessions = sessionsData?.sessions ?? [];
  const { data: currentTerm } = useGetCurrentTermQuery();
  const { data: templatesData } = useGetTemplatesPaginatedQuery({
    page: 1,
    per_page: 100,
  });
  const broadsheetTemplates = (templatesData?.templates ?? []).filter(
    (t) => t.type === "broadsheet",
  );

  // Auto-select current term when session changes
  React.useEffect(() => {
    const terms = formik.values.session?.terms;
    if (!terms || !currentTerm) return;
    const matchingTerm = terms.find((t) => t.id === currentTerm.id);
    formik.setFieldValue("term", matchingTerm ? matchingTerm.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.session?.id, currentTerm?.id]);

  return (
    <div style={{ padding: "0 10px" }}>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          Broadsheet Preview
          <Box>
            <Tooltip title="Print">
              <IconButton
                onClick={() => {
                  const printWindow = window.open("", "Print Broadsheet");
                  if (printWindow) {
                    printWindow.document.write(previewHtml);
                    printWindow.document.close();
                    printWindow.print();
                  }
                }}
              >
                <Print />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setOpenPreview(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "hidden", bgcolor: "#fff" }}>
          <iframe
            title="Broadsheet Preview"
            srcDoc={previewHtml}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              backgroundColor: "#fff",
            }}
          />
        </DialogContent>
      </Dialog>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Report Form
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.class && formik.errors.class}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">CLASS</Typography>
                    <Autocomplete
                      options={classrooms}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={formik.values.class || null}
                      onChange={(_, value) =>
                        formik.setFieldValue("class", value || "")
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setClassroomSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search class..."
                          error={
                            formik.touched.class && Boolean(formik.errors.class)
                          }
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.class && formik.errors.class}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.session && formik.errors.session}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">SESSION</Typography>
                    <Autocomplete
                      options={sessions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={formik.values.session || null}
                      onChange={(_, value) =>
                        formik.setFieldValue("session", value || "")
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setSessionSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search session..."
                          error={
                            formik.touched.session &&
                            Boolean(formik.errors.session)
                          }
                        />
                      )}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.session && formik.errors.session}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.term && formik.errors.term}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TERM</Typography>
                    <StyledSelect
                      displayEmpty
                      id="term"
                      name="term"
                      onChange={formik.handleChange}
                      value={formik.values.term}
                      error={formik.touched.term && Boolean(formik.errors.term)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a term</em>
                      </MenuItem>
                      {formik.values.session?.terms?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.term && formik.errors.term}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">
                      CALCULATION
                    </Typography>
                    <StyledSelect
                      id="calculation"
                      name="calculation"
                      value={formik.values.calculation}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="cumulative">Cumulative</MenuItem>
                    </StyledSelect>
                    <FormHelperText>
                      {formik.values.calculation === "cumulative"
                        ? "Sums each subject's scores, totals, averages and positions across every term in the session up to the selected term."
                        : "Uses only the selected term's scores."}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">TYPE</Typography>
                    <StyledSelect
                      id="assessment_type"
                      name="assessment_type"
                      value={formik.values.assessment_type}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="all">All (CA + Exam)</MenuItem>
                      <MenuItem value="ca">Continuous Assessment (CA)</MenuItem>
                      <MenuItem value="exam">Exam</MenuItem>
                    </StyledSelect>
                    <FormHelperText>
                      {formik.values.assessment_type === "ca"
                        ? "Sums only Continuous Assessment (CA) scores."
                        : formik.values.assessment_type === "exam"
                          ? "Sums only Final Exam scores."
                          : "Sums all Continuous Assessments and Exam scores combined."}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">OUTPUT</Typography>
                    <StyledSelect
                      id="outputMode"
                      name="outputMode"
                      value={formik.values.outputMode}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="download">Download</MenuItem>
                      <MenuItem value="preview">Preview</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.templates && formik.errors.templates}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TEMPLATE</Typography>
                    <StyledSelect
                      id="template"
                      name="template"
                      value={formik.values.template}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.template &&
                        Boolean(formik.errors.template)
                      }
                    >
                      <MenuItem value="">
                        <em>No template (all)</em>
                      </MenuItem>
                      {broadsheetTemplates.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.template && formik.errors.template}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={12}
                  textAlign={"right"}
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  <StyledLoadingButton
                    loading={
                      isCreatingBroadsheet ||
                      isFetchingBroadsheets ||
                      isPolling ||
                      isPreviewing
                    }
                    disabled={isPolling}
                    type="submit"
                  >
                    {isPolling
                      ? "Generating..."
                      : formik.values.outputMode === "preview"
                        ? "Preview"
                        : "Get Report"}
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Generated Table */}

        <Grid item xs={12} md={8}>
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            <Box sx={{ padding: "15px" }}>
              <Typography variant="formHeading">Class Broadsheet</Typography>
              <Typography
                variant="formSubHeading"
                sx={{ textTransform: "none" }}
              >
                Select a class to view broadsheet
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: "auto" }}>
              {isPolling && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {reportProgress?.status || "Generating broadsheet..."}
                    </Typography>
                  </Box>
                  {reportProgress?.percent != null && (
                    <LinearProgress
                      variant="determinate"
                      value={reportProgress.percent}
                    />
                  )}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                  >
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isCancelling}
                      sx={{ textTransform: "none" }}
                    >
                      {isCancelling ? "Cancelling..." : "Cancel"}
                    </Button>
                  </Box>
                </Box>
              )}
              {isMobile ? (
                <Box sx={{ px: 2, pb: 1 }}>
                  {broadsheets.map((item, index) => (
                    <Box
                      key={item.broadsheet?.id || index}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        {item.classroom?.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Preview broadsheet">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleFilePreview(item.broadsheet?.file_url)
                            }
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download broadsheet">
                          <IconButton
                            size="small"
                            component="a"
                            href={item.broadsheet?.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={`${item.classroom?.name}.pdf`}
                          >
                            <PictureAsPdf
                              fontSize="small"
                              style={{ color: "red" }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete broadsheet">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey " }}>
                      <StyledTableCell>CLASS</StyledTableCell>
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {broadsheets.map((item, index) => (
                      <StyledTableRow key={item.broadsheet?.id || index}>
                        <StyledTableCell>
                          {item.classroom?.name}
                        </StyledTableCell>

                        <StyledTableCell>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Preview broadsheet">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleFilePreview(item.broadsheet?.file_url)
                                }
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download broadsheet">
                              <IconButton
                                size="small"
                                component="a"
                                href={item.broadsheet?.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={`${item.classroom?.name}.pdf`}
                              >
                                <PictureAsPdf
                                  fontSize="small"
                                  style={{ color: "red" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete broadsheet">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </StyledPaper>
        </Grid>
      </Grid>
      <ReportPreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </div>
  );
}

export default ClassBroadsheet;
